import { SplatMesh, unpackSplat } from "@sparkjsdev/spark";

/**
 * Resolves which world to load and brings it in.
 *
 * Two sources, deliberately kept behind one interface so the rest of the app
 * never learns where a world came from:
 *
 *   - a generated Marble package under `public/worlds/<id>/world.json`
 *   - a bare splat URL, used before any world has been generated
 *
 * Marble packages carry metric scale and a ground-plane offset. Using them is
 * what puts the world at true human scale instead of an arbitrary guess, and
 * it is the difference between "a room" and "a room that feels like a room".
 */

export interface WorldManifest {
  readonly schemaVersion: 1;
  readonly worldId: string;
  readonly displayName: string;
  readonly prompt: string;
  readonly model: string;
  readonly caption: string | null;
  readonly metricScaleFactor: number | null;
  readonly groundPlaneOffset: number | null;
  readonly files: Readonly<Record<string, number>>;
}

export interface LoadedWorld {
  readonly mesh: SplatMesh;
  readonly splatCount: number;
  readonly loadMs: number;
  readonly name: string;
  readonly caption: string | null;
  /** True when the source shipped an LoD tree, so Spark's pager can engage. */
  readonly hasLod: boolean;
  readonly metricScaleFactor: number | null;
  readonly groundPlaneOffset: number | null;
}

/** Spark's own published sample. Used only until a world is generated. */
const FALLBACK_SPLAT = "https://sparkjs.dev/assets/splats/butterfly.spz";

async function loadManifest(folder: string): Promise<WorldManifest> {
  const response = await fetch(`/worlds/${folder}/world.json`, {
    cache: "no-cache"
  });
  if (!response.ok) {
    throw new Error(`world manifest ${folder} -> ${response.status}`);
  }
  return (await response.json()) as WorldManifest;
}

function pickSplatFile(
  manifest: WorldManifest,
  quality: "100k" | "500k" | "full"
): string {
  const preference =
    quality === "full"
      ? ["world-full.spz", "world-500k.spz", "world-100k.spz"]
      : quality === "100k"
        ? ["world-100k.spz", "world-500k.spz", "world-full.spz"]
        : ["world-500k.spz", "world-100k.spz", "world-full.spz"];
  return preference.find((f) => f in manifest.files) ?? "world-500k.spz";
}

export async function loadWorld(options: {
  readonly folder: string | null;
  readonly url: string | null;
  readonly quality: "100k" | "500k" | "full";
  readonly onProgress: (message: string) => void;
}): Promise<LoadedWorld> {
  const started = performance.now();

  if (options.folder !== null) {
    const manifest = await loadManifest(options.folder);
    const file = pickSplatFile(manifest, options.quality);
    options.onProgress(`loading ${manifest.displayName} · ${file}`);

    const mesh = new SplatMesh({ url: `/worlds/${options.folder}/${file}` });
    // Splat captures use a Y-down (OpenCV) convention; this is the standard
    // half turn about X into the Y-up world Three.js renders in.
    mesh.quaternion.set(1, 0, 0, 0);
    await mesh.initialized;

    return {
      mesh,
      splatCount: mesh.packedSplats?.numSplats ?? 0,
      loadMs: Math.round(performance.now() - started),
      name: manifest.displayName,
      caption: manifest.caption,
      // Marble's standard SPZ exports are plain splat files. Spark's LoD tree
      // is a separate build step, so claiming LoD here would be false.
      hasLod: false,
      metricScaleFactor: manifest.metricScaleFactor,
      groundPlaneOffset: manifest.groundPlaneOffset
    };
  }

  const url = options.url ?? FALLBACK_SPLAT;
  options.onProgress(`loading ${url.split("/").pop() ?? url}`);
  const mesh = new SplatMesh({ url });
  mesh.quaternion.set(1, 0, 0, 0);
  await mesh.initialized;

  return {
    mesh,
    splatCount: mesh.packedSplats?.numSplats ?? 0,
    loadMs: Math.round(performance.now() - started),
    name: url === FALLBACK_SPLAT ? "Spark sample" : "Custom splat",
    caption: null,
    // A bare URL may or may not carry an LoD tree. Assume not rather than
    // claim a capability we have not verified.
    hasLod: false,
    metricScaleFactor: null,
    groundPlaneOffset: null
  };
}

/**
 * Estimates a splat cloud's extent by sampling decoded splat centres.
 *
 * `Box3.setFromObject` cannot do this: a SplatMesh is a bare Object3D with no
 * BufferGeometry, so three sees an empty box and reports a radius of zero. The
 * splat positions only exist inside the packed buffer, so we decode a sample of
 * them instead of every one — a few thousand is ample for an extent estimate
 * and avoids stalling the first frame on a half-million-splat decode.
 *
 * The 95th percentile radius is used rather than the maximum because splat
 * captures routinely contain a handful of far-flung outlier splats, and letting
 * those set the bounds would frame every world far too small.
 */
/**
 * Finds the world's floor height by sampling splat positions.
 *
 * Marble reports a ground-plane offset, but consuming it correctly means
 * knowing the sign convention it was authored in *and* accounting for the
 * 180-degree X rotation applied to convert the capture to Y-up — get either
 * wrong and the floor ends up buried or floating. Measuring the distribution
 * directly sidesteps both questions.
 *
 * A percentile is used rather than the minimum because captures contain stray
 * splats below the real floor, and outdoor scenes contain genuine geometry
 * below the walkable surface — a pond bed, falling terrain. Taking the very
 * bottom lands the viewer floating above the path they should be standing on,
 * so the default sits high enough to find the ground people actually walk on.
 * Tunable via `?floor=`.
 * Returns the height in the mesh's post-rotation local frame, so shifting the
 * mesh by its negation puts the floor at y=0.
 */
export function measureGroundY(
  mesh: SplatMesh,
  percentile = 0.15,
  sampleSize = 6000
): number | null {
  const packed = mesh.packedSplats;
  const array = packed?.packedArray;
  const count = packed?.numSplats ?? 0;
  if (array == null || count === 0) {
    return null;
  }
  const step = Math.max(1, Math.floor(count / sampleSize));
  const heights: number[] = [];
  for (let i = 0; i < count; i += step) {
    // The mesh is rotated a half turn about X, which negates Y.
    heights.push(-unpackSplat(array, i).center.y);
  }
  if (heights.length === 0) {
    return null;
  }
  heights.sort((a, b) => a - b);
  const index = Math.min(
    heights.length - 1,
    Math.max(0, Math.floor(heights.length * percentile))
  );
  return heights[index] ?? null;
}

export interface WorldFrame {
  /** Horizontal centre of mass, in the mesh's post-rotation local frame. */
  readonly centreX: number;
  readonly centreZ: number;
  /** Height of the walkable surface in the same frame. */
  readonly floorY: number;
  /** Distance from the centre containing most of the world. */
  readonly radius: number;
}

/**
 * Measures where a world actually sits, so the viewer can be placed in the
 * middle of it standing on its ground.
 *
 * Everything is computed in the mesh's post-rotation frame — the mesh carries a
 * half turn about X to convert the capture's Y-down convention, which negates
 * both Y and Z — so the caller can use these numbers directly without having to
 * reason about the flip again.
 *
 * Medians, not means: a handful of splats flung far from the scene will drag a
 * mean badly off centre, and the resulting placement drops you somewhere out in
 * empty space beside the world instead of inside it.
 */
export function measureWorldFrame(
  mesh: SplatMesh,
  floorPercentile = 0.08,
  sampleSize = 8000
): WorldFrame | null {
  const packed = mesh.packedSplats;
  const array = packed?.packedArray;
  const count = packed?.numSplats ?? 0;
  if (array == null || count === 0) {
    return null;
  }
  const step = Math.max(1, Math.floor(count / sampleSize));
  const xs: number[] = [];
  const ys: number[] = [];
  const zs: number[] = [];
  for (let i = 0; i < count; i += step) {
    const c = unpackSplat(array, i).center;
    xs.push(c.x);
    ys.push(-c.y);
    zs.push(-c.z);
  }
  if (xs.length === 0) {
    return null;
  }
  const at = (values: number[], fraction: number): number => {
    const sorted = [...values].sort((a, b) => a - b);
    const index = Math.min(
      sorted.length - 1,
      Math.max(0, Math.floor(sorted.length * fraction))
    );
    return sorted[index] ?? 0;
  };

  const centreX = at(xs, 0.5);
  const centreZ = at(zs, 0.5);
  const floorY = at(ys, floorPercentile);

  // Radius from the centre, again at a percentile so outliers do not inflate it.
  const distances = xs.map((x, i) =>
    Math.hypot(x - centreX, (zs[i] ?? 0) - centreZ)
  );
  return { centreX, centreZ, floorY, radius: at(distances, 0.9) };
}

export function measureSplatExtent(mesh: SplatMesh, sampleSize = 4000): number {
  const packed = mesh.packedSplats;
  const array = packed?.packedArray;
  const count = packed?.numSplats ?? 0;
  if (array == null || count === 0) {
    return 0;
  }
  const step = Math.max(1, Math.floor(count / sampleSize));
  const radii: number[] = [];
  for (let i = 0; i < count; i += step) {
    const splat = unpackSplat(array, i);
    radii.push(splat.center.length());
  }
  if (radii.length === 0) {
    return 0;
  }
  radii.sort((a, b) => a - b);
  const index = Math.min(radii.length - 1, Math.floor(radii.length * 0.95));
  return radii[index] ?? 0;
}
